import { Briefcase, MapPin, ChevronDown, Quote, Award } from "lucide-react";
import { useState } from "react";
import { CAREER_TIMELINE } from "@/data/journey";
import useReveal from "@/hooks/useReveal";

/**
 * Chapter II — Dad's civilian career after the Navy.
 *
 * Each entry is a "stage" of his career (employer or contractor era).
 * Stages with a `projects` array render an expandable list of major
 * client engagements (used for the long Murrayfield / IM Contractor era).
 */
export default function Career() {
  return (
    <section
      id="career"
      className="relative z-10 pb-16 sm:pb-24 md:pb-32 bg-[var(--c-bg)] scroll-mt-16 sm:scroll-mt-20"
      data-testid="career-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        <div className="relative">
          {/* Center vertical line (desktop) */}
          <div
            className="hidden md:block absolute left-1/2 -translate-x-px top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[var(--c-accent)]/45 to-transparent"
            aria-hidden="true"
          />
          <ol className="space-y-12 sm:space-y-20 md:space-y-24">
            {CAREER_TIMELINE.map((role, idx) => (
              <CareerEntry key={role.id} role={role} idx={idx} />
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

function CareerEntry({ role, idx }) {
  const ref = useReveal();
  const isRight = idx % 2 === 1;
  const hasProjects = Array.isArray(role.projects) && role.projects.length > 0;
  const hasHonours = Array.isArray(role.honours) && role.honours.length > 0;
  const [projectsOpen, setProjectsOpen] = useState(false);

  return (
    <li
      ref={ref}
      className="reveal relative"
      data-testid={`career-entry-${role.id}`}
    >
      <div className="md:grid md:grid-cols-2 md:gap-16 items-start">
      {/* Connector dot */}
      <span
        className="hidden md:block absolute left-1/2 -translate-x-1/2 top-6 w-3 h-3 rounded-full bg-[var(--c-accent)] ring-4 ring-[var(--c-bg)] z-10"
        aria-hidden="true"
      />

      {/* Year side */}
      <div
        className={`${isRight ? "md:order-2 md:pl-8" : "md:order-1 md:text-right md:pr-8"}`}
      >
        <div
          className={`flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 ${isRight ? "" : "md:justify-end"}`}
        >
          <span className="font-mono-plex text-[10px] sm:text-xs text-[var(--c-text-muted)]">
            {String(idx + 1).padStart(2, "0")} / {CAREER_TIMELINE.length}
          </span>
          <span className="w-6 sm:w-8 h-px bg-[var(--c-accent)]/50" />
          <span className="overline truncate">Stage</span>
        </div>
        <p className="font-serif-display text-2xl sm:text-3xl md:text-4xl text-[var(--c-accent)] tracking-tight italic leading-[0.95]">
          {role.dates}
        </p>
        {role.location && (
          <p
            className={`mt-3 font-mono-plex text-[11px] sm:text-xs uppercase tracking-[0.16em] text-[var(--c-text-muted)] ${isRight ? "" : "md:justify-end"} inline-flex items-center gap-2`}
          >
            <MapPin size={12} strokeWidth={1.8} />
            <span>{role.location}</span>
          </p>
        )}
      </div>

      {/* Card side */}
      <div
        className={`mt-4 md:mt-0 ${isRight ? "md:order-1 md:pr-8" : "md:order-2 md:pl-8"}`}
      >
        <div className="border border-[var(--c-border)] bg-[var(--c-bg-elev)] p-5 sm:p-6 md:p-7">
          <div className="flex items-start gap-3 mb-3 sm:mb-4">
            <span
              className="mt-1 w-9 h-9 shrink-0 border border-[var(--c-accent)]/50 flex items-center justify-center text-[var(--c-accent)]"
              aria-hidden="true"
            >
              <Briefcase size={16} strokeWidth={1.8} />
            </span>
            <div className="min-w-0">
              <h3 className="font-serif-display text-lg sm:text-xl md:text-2xl text-[var(--c-text)] leading-tight">
                {role.role}
              </h3>
              <p className="font-mono-plex text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[var(--c-accent)] mt-1">
                {role.employer}
              </p>
            </div>
          </div>
          <p className="text-sm sm:text-base text-[var(--c-text-muted)] leading-relaxed">
            {role.summary}
          </p>
          {role.detail && (
            <p className="mt-3 sm:mt-4 text-sm text-[var(--c-text-muted)] leading-relaxed italic">
              {role.detail}
            </p>
          )}

          {hasProjects && (
            <div className="mt-5 pt-5 border-t border-[var(--c-border)]">
              <button
                onClick={() => setProjectsOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 font-mono-plex text-[11px] sm:text-xs uppercase tracking-[0.2em] text-[var(--c-accent)] hover:text-[var(--c-accent-bright)] py-2 min-h-[44px] transition-colors"
                data-testid={`career-${role.id}-projects-toggle`}
                aria-expanded={projectsOpen}
              >
                <span>
                  {projectsOpen ? "Hide" : "Show"}{" "}
                  {role.projects.length} major engagements
                </span>
                <ChevronDown
                  size={14}
                  className={`transition-transform ${projectsOpen ? "rotate-180" : ""}`}
                />
              </button>

              {projectsOpen && (
                <ol
                  className="mt-5 space-y-7 sm:space-y-9"
                  data-testid={`career-${role.id}-projects`}
                >
                  {role.projects.map((p, i) => (
                    <li
                      key={p.id}
                      className="relative pl-5 sm:pl-6 border-l-2 border-[var(--c-accent)]/40"
                      data-testid={`career-project-${p.id}`}
                    >
                      <span
                        className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[var(--c-accent)] ring-4 ring-[var(--c-bg-elev)]"
                        aria-hidden="true"
                      />
                      <p className="font-mono-plex text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[var(--c-accent)]">
                        {p.dates}
                      </p>
                      <h4 className="font-serif-display text-base sm:text-lg md:text-xl text-[var(--c-text)] mt-1 leading-snug">
                        {p.client}
                      </h4>
                      {p.location && (
                        <p className="text-[11px] sm:text-xs text-[var(--c-text-muted)] mt-1 italic">
                          {p.location}
                        </p>
                      )}
                      <p className="mt-2.5 text-sm text-[var(--c-text-muted)] leading-relaxed">
                        {p.body}
                      </p>
                      {p.aside && (
                        <blockquote
                          className="mt-3 pl-3 sm:pl-4 border-l border-[var(--c-accent)]/40"
                          data-testid={`career-project-${p.id}-aside`}
                        >
                          <span
                            className="inline-block mr-2 text-[var(--c-accent)] align-text-top"
                            aria-hidden="true"
                          >
                            <Quote size={10} />
                          </span>
                          <em className="font-serif-display text-sm text-[var(--c-text)]">
                            {p.aside}
                          </em>
                          <span className="block font-mono-plex text-[9px] uppercase tracking-[0.22em] text-[var(--c-text-muted)] mt-1">
                            — Dad
                          </span>
                        </blockquote>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {hasHonours && (
        <div
          className="mt-8 sm:mt-10 md:mt-12"
          data-testid={`career-${role.id}-honours`}
        >
          {role.honours.map((h) => (
            <HonourPanel key={h.id} honour={h} />
          ))}
        </div>
      )}
    </li>
  );
}

function HonourPanel({ honour }) {
  const primary = honour.images?.[0];
  const rest = honour.images?.slice(1) || [];

  return (
    <figure
      className="border border-[var(--c-accent)]/40 bg-[var(--c-bg-elev)] overflow-hidden"
      data-testid={`honour-${honour.id}`}
    >
      <div className="p-5 sm:p-6 md:p-8 border-b border-[var(--c-border)] flex items-start gap-4">
        <span
          className="mt-1 w-11 h-11 shrink-0 border border-[var(--c-accent)]/60 flex items-center justify-center text-[var(--c-accent)]"
          aria-hidden="true"
        >
          <Award size={20} strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-mono-plex text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-[var(--c-accent)]">
            A Civic Honour · {honour.date}
          </p>
          <h4 className="font-serif-display text-xl sm:text-2xl md:text-3xl text-[var(--c-text)] mt-2 leading-snug">
            {honour.title}
          </h4>
          {honour.motto && (
            <p className="font-serif-display italic text-[var(--c-accent)] mt-1 text-sm sm:text-base">
              “{honour.motto}”
            </p>
          )}
        </div>
      </div>

      {primary && (
        <div className="grid md:grid-cols-2 gap-0 border-b border-[var(--c-border)]">
          <div className="relative bg-[var(--c-bg-elev-2)] aspect-[4/5] md:aspect-auto md:min-h-[420px] flex items-center justify-center overflow-hidden">
            {primary.isDad && (
              <span className="dad-badge absolute top-3 left-3 z-10">
                Dad&apos;s photo
              </span>
            )}
            <img
              src={primary.src}
              alt={primary.caption}
              loading="lazy"
              className="archival-img w-full h-full object-cover"
            />
          </div>
          {rest[0] && (
            <div className="relative bg-[var(--c-bg-elev-2)] aspect-[4/5] md:aspect-auto md:min-h-[420px] flex items-center justify-center overflow-hidden border-t md:border-t-0 md:border-l border-[var(--c-border)]">
              <img
                src={rest[0].src}
                alt={rest[0].caption}
                loading="lazy"
                className="w-full h-full object-contain p-4 sm:p-6"
                style={{ filter: "sepia(0.05)" }}
              />
            </div>
          )}
        </div>
      )}

      <div className="p-5 sm:p-6 md:p-8">
        <p className="text-sm sm:text-base text-[var(--c-text-muted)] leading-relaxed">
          {honour.body}
        </p>
        {honour.images && honour.images.length > 0 && (
          <ul className="mt-5 space-y-1.5 pt-4 border-t border-[var(--c-border)]">
            {honour.images.map((img, i) => (
              <li
                key={i}
                className="flex gap-3 text-xs sm:text-sm text-[var(--c-text-muted)] italic leading-relaxed"
              >
                <span className="font-mono-plex text-[10px] text-[var(--c-accent)] shrink-0 mt-0.5">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{img.caption}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </figure>
  );
}
